import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { ChevronLeftIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import CountryFlag from '../../../../components/elements/country-flag'
import CenterEventActionLog from '../../../../components/pages/center-events/center-event-action-log'
import CenterEventBasicInfoBox from '../../../../components/pages/center-events/center-event-basic-info-box'
import CenterEventCenterInformation from '../../../../components/pages/center-events/center-event-center-information'
import CenterEventDescriptionSection from '../../../../components/pages/center-events/center-event-description-section'
import CenterEventFilesLinkDisplay from '../../../../components/pages/center-events/center-event-files-link-display'
import CenterEventHeader from '../../../../components/pages/center-events/center-event-header'
import CenterEventStudentInformation from '../../../../components/pages/center-events/center-event-student-information'
import { Paths } from '../../../../lib/constants/path'
import { CenterEvent } from '../../../../lib/types/models/center-event/center-event'
import { CenterEventMode } from '../../../../lib/types/models/center-event/center-event-input'
import { CenterEventProperties } from '../../../../lib/types/models/center-event/properties.center-event'
import { CenterToJourneyProperties } from '../../../../lib/types/models/center-to-journey/properties.center-to-journey'

type Props = {
  centerEvent: CenterEvent
  mode?: CenterEventMode
  className?: string
}

const CenterEventData = ({ centerEvent, className, mode = 'NORMAL' }: Props) => {
  const { navigate } = useNavigator()

  const center = centerEvent.center

  const getJourneyLabel = (
    centerToJourney: Parameters<typeof CenterToJourneyProperties.getFormulaName>[0] &
      Parameters<typeof CenterToJourneyProperties.getThemeName>[0] &
      Parameters<typeof CenterToJourneyProperties.getOptions>[0]
  ) =>
    [
      CenterToJourneyProperties.getFormulaName(centerToJourney),
      CenterToJourneyProperties.getThemeName(centerToJourney),
      ...CenterToJourneyProperties.getOptions(centerToJourney)
    ].join(' - ')

  // An enrollment event shows only the youth's own stay, a center event lists every stay of the center
  const studentProposition = centerEvent.student?.mainProposition
  const formulas = studentProposition
    ? getJourneyLabel(studentProposition)
    : center?.centerToJourneys
        .map(getJourneyLabel)
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(', ')

  const { startDate, endDate } = CenterEventProperties.getCenterDateRange(center)

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex gap-4 items-center">
        {mode === 'NORMAL' && (
          <Button
            icon={ChevronLeftIcon}
            label="Retour"
            variant="secondary"
            onClick={() => {
              navigate(Paths.CENTER_EVENT_LIST_PAGE, { replace: true })
            }}
          />
        )}
        <div className="flex gap-2 items-center">
          <Typography variant="h4">{formulas}</Typography>
          <CountryFlag possibleCountry={center?.journeyPlace?.country.name} />
          {startDate && endDate && (
            <Badge
              size="medium"
              text={`${DateFormatter.DDMMYYYY(startDate)} - ${DateFormatter.DDMMYYYY(endDate)}`}
            />
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <CenterEventBasicInfoBox centerEvent={centerEvent} mode={mode} />
      </div>
      <CenterEventHeader centerEvent={centerEvent} mode={mode} />
      {centerEvent.student ? (
        <CenterEventStudentInformation studentInfo={centerEvent.student} />
      ) : (
        <CenterEventCenterInformation center={center} />
      )}
      <CenterEventFilesLinkDisplay files={centerEvent.files} isBadge />
      <CenterEventDescriptionSection description={centerEvent.description} />
      <CenterEventActionLog centerEvent={centerEvent} mode={mode} />
    </div>
  )
}

export default CenterEventData
